import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Calendar, Heart } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section className="relative pt-24 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-white" data-id="element-103">
      {/* Background Gradients */}
      <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-brand-light to-transparent opacity-60 -z-10" data-id="element-104"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-105">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-106">
          {/* Left Column: Content */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-107">
            {/* Eyebrow Badge */}
            <div className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-8 tracking-wide" data-id="element-108">
              <Heart className="w-4 h-4 fill-current" data-id="element-109" />
              {t('hero.badge')}
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-brand-deep leading-[1.1] mb-5 font-heading" data-id="element-110">
              {t('hero.title')}
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-gray-500 mb-8 leading-relaxed max-w-lg font-light" data-id="element-111">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8" data-id="element-112">
              <a href="#contact" className="bg-brand-teal hover:bg-brand-deep text-white px-6 py-3.5 rounded-full font-semibold text-base lg:text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 group" data-id="element-113">
                <Calendar className="w-5 h-5 group-hover:scale-110 transition-transform" data-id="element-114" />
                {t('hero.cta.request')}
              </a>
              <a href="tel:6103747035" className="bg-transparent hover:bg-brand-sky/20 text-brand-teal border-2 border-brand-teal px-6 py-3.5 rounded-full font-semibold text-base lg:text-lg transition-all flex items-center justify-center gap-2" data-id="element-115">
                <Phone className="w-5 h-5" data-id="element-116" />
                {t('hero.cta.call')}
              </a>
            </div>

            {/* Trust Stats */}
            <div className="flex items-center gap-4 sm:gap-8 border-t border-gray-100 pt-6" data-id="element-117">
              <div data-id="element-118">
                <p className="font-heading font-bold text-brand-teal text-2xl sm:text-3xl" data-id="element-119">
                  500+
                </p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mt-1" data-id="element-120">
                  {t('hero.stat.patients')}
                </p>
              </div>
              <div className="w-px h-8 bg-gray-200" data-id="element-121"></div>
              <div data-id="element-122">
                <p className="font-heading font-bold text-brand-teal text-2xl sm:text-3xl" data-id="element-123">
                  5 Days
                </p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mt-1" data-id="element-124">
                  {t('hero.stat.wait')}
                </p>
              </div>
              <div className="w-px h-8 bg-gray-200" data-id="element-125"></div>
              <div data-id="element-126">
                <p className="font-heading font-bold text-brand-teal text-xl sm:text-3xl" data-id="element-127">
                  Bilingual
                </p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mt-1" data-id="element-128">
                  {t('hero.stat.staff')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Visual */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative mt-6 lg:mt-0" data-id="element-129">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-[6px] lg:border-[8px] border-white bg-gradient-to-br from-brand-sky/30 via-white to-brand-teal/10 flex flex-col items-center justify-center p-6 md:p-12 py-10" data-id="element-130">
              {/* Decorative background blobs */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-brand-sky/40 rounded-full blur-3xl -z-0" data-id="element-131"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-brand-lilac/20 rounded-full blur-3xl -z-0" data-id="element-132"></div>

              {/* Logo prominently displayed */}
              <div className="relative z-10 flex flex-col items-center gap-8" data-id="element-133">
                <img src="https://cdn.magicpatterns.com/uploads/4sxQ2e4dUAxRWLCzqVdgyT/bohevia.jpg" alt="Reading Behavioral Health Center" className="w-full max-w-sm h-auto object-contain drop-shadow-lg" data-id="element-134" />

                {/* Trust badge below logo */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-md border border-white/60 text-center" data-id="element-135">
                  <p className="text-brand-teal font-semibold text-sm tracking-wide" data-id="element-136">
                    Reading, PA · Est. 2010
                  </p>
                  <p className="text-brand-deep font-bold text-base mt-1" data-id="element-137">
                    Compassionate Care for All Ages
                  </p>
                </div>
              </div>
            </div>

            {/* Decorative Elements behind card */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-brand-teal/10 rounded-full blur-3xl -z-10" data-id="element-138"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-brand-lilac/10 rounded-full blur-3xl -z-10" data-id="element-139"></div>
          </motion.div>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="wave-divider text-white absolute bottom-0 left-0 w-full" data-id="element-140">
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px] md:h-[100px] fill-current block" data-id="element-141">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" data-id="element-142"></path>
        </svg>
      </div>
    </section>;
}